// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { isUnsafePropKey, objCreate, objHasOwn, scheduleTimeout } from "@nevware21/ts-utils";
import { _DYN_CANCEL, _DYN_CONFIG, _DYN_INSTRUMENTATION_KEY, _DYN_LENGTH, _DYN_NAME, _DYN_REPLACE, _DYN_TRACK } from "../__DynamicConstants";
import { MetricDataType } from "../telemetry/ai/DataTypes";
var FLUSH_INTERVAL = 900000; // 15 min default
var MET_SUCCESS = "Item_Success_Count";
var MET_DROPPED = "Item_Dropped_Count";
var MET_RETRY = "Item_Retry_Count";
var DROP_CLIENT_EXCEPTION = "CLIENT_EXCEPTION";
var DEFAULT_TEL_TYPE = "CUSTOM_EVENT";
// Top-level event name for AI stats. Matches the standard AI event naming
// (Microsoft.ApplicationInsights.<iKey>.<Type>).
var AI_STATS_PREFIX = "Microsoft.ApplicationInsights.";
var AI_STATS_SUFFIX = "SdkStats";
// Map baseType to spec telemetryType values
var _typeMap = {
    "EventData": DEFAULT_TEL_TYPE,
    "MetricData": "CUSTOM_METRIC",
    "RemoteDependencyData": "DEPENDENCY",
    "ExceptionData": "EXCEPTION",
    "PageviewData": "PAGE_VIEW",
    "PageviewPerformanceData": "PAGE_VIEW",
    "MessageData": "TRACE",
    "RequestData": "REQUEST",
    "AvailabilityData": "AVAILABILITY",
    "PageActionData": DEFAULT_TEL_TYPE,
    "ContentUpdateData": DEFAULT_TEL_TYPE,
    "PageUnloadData": DEFAULT_TEL_TYPE
};
/**
 * Creates an INotificationListener that accumulates success/dropped/retry counts and periodically
 * flushes them as Item_Success_Count, Item_Dropped_Count, and Item_Retry_Count metrics via core.track().
 * Reads config.sdkStats.int dynamically from core.config on each flush.
 * @param core - The IAppInsightsCore instance (provides track() and config)
 * @param sdkVersion - The SDK version string to include in reported metrics
 * @param eventName - Optional top-level event name (e.g. "Ms.Web.SdkStat" for 1DS). Defaults to the
 * standard AI event name "Microsoft.ApplicationInsights.[iKey].SdkStats". The counter name
 * (Item_Success_Count etc.) is kept in baseData.name and the metricName property, never the
 * top-level name.
 * @returns An INotificationListener with flush and unload methods
 */
/*#__NO_SIDE_EFFECTS__*/
export function createSdkStatsNotifCbk(core, sdkVersion, eventName) {
    var _successCounts = objCreate(null);
    var _droppedCounts = objCreate(null);
    var _retryCounts = objCreate(null);
    var _timer;
    var _interval = (core[_DYN_CONFIG /* @min:%2econfig */].sdkStats && core[_DYN_CONFIG /* @min:%2econfig */].sdkStats.int) || FLUSH_INTERVAL;
    // Static across every metric in a flush, so compute once at creation time.
    var _version = sdkVersion || "unknown";
    // Top-level event name for every stat. 1DS passes its own name (e.g. "Ms.Web.SdkStat"); AI builds
    // a "Microsoft.ApplicationInsights.<iKey>.SdkStats" name to match standard AI events.
    var _statsEventName = eventName || _buildAiStatsEventName();
    // Once unloaded, late notification callbacks must be no-ops and the timer must not re-arm.
    var _unloaded = false;
    // Builds the default AI stats event name, including the instrumentation key (dashes stripped)
    // when available so it matches the Microsoft.ApplicationInsights.<iKey>.<Type> convention.
    function _buildAiStatsEventName() {
        var iKey = core.config && core.config[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */];
        var iKeySeg = (typeof iKey === "string" && iKey) ? iKey[_DYN_REPLACE /* @min:%2ereplace */](/-/g, "") + "." : "";
        return AI_STATS_PREFIX + iKeySeg + AI_STATS_SUFFIX;
    }
    function _ensureTimer() {
        if (!_timer && !_unloaded) {
            _timer = scheduleTimeout(_flush, _interval);
        }
    }
    function _getTelType(item) {
        var bt = item.baseType;
        return (bt && objHasOwn(_typeMap, bt) && _typeMap[bt]) || DEFAULT_TEL_TYPE;
    }
    function _isSdkStatsMetric(item) {
        // Self-filter our own emitted stats by their event name.
        return item[_DYN_NAME /* @min:%2ename */] === _statsEventName;
    }
    function _incSuccess(items) {
        if (_unloaded || !items || !items[_DYN_LENGTH /* @min:%2elength */]) {
            return;
        }
        var changed = false;
        for (var i = 0; i < items[_DYN_LENGTH /* @min:%2elength */]; i++) {
            if (!_isSdkStatsMetric(items[i])) {
                var t = _getTelType(items[i]);
                if (!isUnsafePropKey(t)) {
                    // _successCounts is a null-prototype object (objCreate(null)) so this cannot pollute Object.prototype
                    _successCounts[t] = (_successCounts[t] || 0) + 1;
                    changed = true;
                }
            }
        }
        if (changed) {
            _ensureTimer();
        }
    }
    /**
     * Common helper to increment a bucketed counter (dropped or retry) keyed by code and telemetry type.
     */
    function _incBucketed(counters, items, code) {
        if (_unloaded || !(items && items[_DYN_LENGTH /* @min:%2elength */] && !isUnsafePropKey(code))) {
            return;
        }
        var bucket = counters[code];
        if (!bucket) {
            bucket = counters[code] = objCreate(null);
        }
        var changed = false;
        for (var i = 0; i < items[_DYN_LENGTH /* @min:%2elength */]; i++) {
            if (!_isSdkStatsMetric(items[i])) {
                var t = _getTelType(items[i]);
                if (!isUnsafePropKey(t)) {
                    // bucket is a null-prototype object (objCreate(null)) so this cannot pollute Object.prototype
                    bucket[t] = (bucket[t] || 0) + 1;
                    changed = true;
                }
            }
        }
        if (changed) {
            _ensureTimer();
        }
    }
    function _createMetric(name, value, telType, code, codePropKey) {
        var props = {
            telemetryType: telType,
            computeType: "unknown",
            language: "JavaScript",
            version: _version,
            // Counter name as a property so the success/dropped/retry distinction survives.
            metricName: name
        };
        if (code && codePropKey) {
            props[codePropKey] = code;
        }
        // Top-level name is always the dedicated stats event; counter name stays in baseData.name.
        return {
            name: _statsEventName,
            baseType: MetricDataType,
            baseData: {
                name: name,
                average: value,
                sampleCount: 1,
                properties: props
            }
        };
    }
    function _mapDropCode(reason, sendType) {
        // Maps eEventsDiscardedReason to spec dropCode values
        // 1 = NonRetryableStatus → actual HTTP status code when available
        if (reason === 1 && sendType) {
            return "" + sendType;
        }
        return DROP_CLIENT_EXCEPTION;
    }
    /**
     * Common helper to flush bucketed counters (dropped or retry).
     * @param counters - The bucketed counter object
     * @param metricName - The metric name to emit (e.g. MET_DROPPED or MET_RETRY)
     * @param codePropKey - The property key for the code dimension (e.g. "dropCode" or "retryCode")
     */
    function _flushBucketed(counters, metricName, codePropKey) {
        for (var code in counters) {
            if (objHasOwn(counters, code)) {
                var bucket = counters[code];
                for (var telType in bucket) {
                    if (objHasOwn(bucket, telType)) {
                        var cnt = bucket[telType];
                        if (cnt > 0) {
                            core[_DYN_TRACK /* @min:%2etrack */](_createMetric(metricName, cnt, telType, code, codePropKey));
                        }
                    }
                }
            }
        }
    }
    function _flush() {
        if (_timer) {
            _timer[_DYN_CANCEL /* @min:%2ecancel */]();
            _timer = null;
        }
        // Re-read interval from core.config in case it changed dynamically
        var statsCfg = (core[_DYN_CONFIG /* @min:%2econfig */] && core[_DYN_CONFIG /* @min:%2econfig */].sdkStats) || {};
        _interval = statsCfg.int || FLUSH_INTERVAL;
        // Flush success counts
        for (var telType in _successCounts) {
            if (objHasOwn(_successCounts, telType)) {
                var cnt = _successCounts[telType];
                if (cnt > 0) {
                    core[_DYN_TRACK /* @min:%2etrack */](_createMetric(MET_SUCCESS, cnt, telType));
                }
            }
        }
        // Flush dropped and retry counts via common helper
        _flushBucketed(_droppedCounts, MET_DROPPED, "dropCode");
        _flushBucketed(_retryCounts, MET_RETRY, "retryCode");
        // Reset accumulators for the next interval
        _successCounts = objCreate(null);
        _droppedCounts = objCreate(null);
        _retryCounts = objCreate(null);
    }
    return {
        eventsSent: _incSuccess,
        eventsDiscarded: function (events, reason, sendType) {
            var code = _mapDropCode(reason, sendType);
            _incBucketed(_droppedCounts, events, code);
        },
        eventsRetry: function (events, statusCode) {
            var code = "" + statusCode; // numeric status code as string per spec
            _incBucketed(_retryCounts, events, code);
        },
        flush: _flush,
        unload: function () {
            // Mark unloaded FIRST so any synchronous notification callbacks fired by
            // core.track() during the flush below (while this listener is still
            // registered) become no-ops and the timer cannot re-arm. Then flush the
            // accumulated counts one final time.
            _unloaded = true;
            _flush();
        }
    };
}
//# sourceMappingURL=SdkStatsNotificationCbk.js.map